/**
 * Hook to generate effect preview thumbnails from the current artwork image.
 * Uses OffscreenCanvas to generate tiny preview images with each effect applied.
 */

import { useState, useEffect } from 'react';
import { EFFECT_PRESETS, getEffectDefaults, type EffectPresetDef } from './presets';

const THUMB_SIZE = 64;

/**
 * Generate a tiny thumbnail with a CSS filter applied.
 * Uses a canvas element to draw the image with the filter.
 */
function generateFilteredThumbnail(img: HTMLImageElement, preset: EffectPresetDef): string {
  const canvas = document.createElement('canvas');
  canvas.width = THUMB_SIZE;
  canvas.height = THUMB_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  // Compute effect CSS
  const defaults = getEffectDefaults(preset);
  const css = preset.applyCSS(defaults);

  // Apply CSS filter on the canvas context
  if (css.filter) {
    ctx.filter = css.filter;
  }

  // Draw the image cropped to center square
  const size = Math.min(img.width, img.height);
  const sx = (img.width - size) / 2;
  const sy = (img.height - size) / 2;
  ctx.drawImage(img, sx, sy, size, size, 0, 0, THUMB_SIZE, THUMB_SIZE);

  // Reset filter for overlay drawing
  ctx.filter = 'none';

  // Draw overlay if present (parse simple gradients as color fills)
  if (css.overlay && css.overlayBlendMode) {
    ctx.globalCompositeOperation = mapBlendMode(css.overlayBlendMode);
    ctx.globalAlpha = css.overlayOpacity ?? 1;

    // For gradient overlays, approximate with a solid color or gradient
    if (css.overlay.includes('radial-gradient')) {
      // Approximate vignette with a dark border
      const grad = ctx.createRadialGradient(
        THUMB_SIZE / 2,
        THUMB_SIZE / 2,
        THUMB_SIZE * 0.3,
        THUMB_SIZE / 2,
        THUMB_SIZE / 2,
        THUMB_SIZE * 0.7,
      );
      grad.addColorStop(0, 'transparent');
      grad.addColorStop(1, 'rgba(0,0,0,0.4)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, THUMB_SIZE, THUMB_SIZE);
    } else if (css.overlay.includes('linear-gradient')) {
      // Approximate with a semi-transparent tint
      ctx.fillStyle = 'rgba(255,200,100,0.15)';
      ctx.fillRect(0, 0, THUMB_SIZE, THUMB_SIZE);
    }

    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  }

  return canvas.toDataURL('image/jpeg', 0.6);
}

/** Map CSS blend mode names to canvas composite operations */
function mapBlendMode(mode: string): GlobalCompositeOperation {
  const map: Record<string, GlobalCompositeOperation> = {
    normal: 'source-over',
    multiply: 'multiply',
    screen: 'screen',
    overlay: 'overlay',
    'soft-light': 'soft-light',
    'hard-light': 'hard-light',
    'color-dodge': 'color-dodge',
    'color-burn': 'color-burn',
    difference: 'difference',
    exclusion: 'exclusion',
  };
  return map[mode] ?? 'source-over';
}

/**
 * Hook: generates a Map of effectId → thumbnail dataURL
 * Re-generates when the image URL changes.
 */
export function useEffectThumbnails(imageUrl: string | null): Map<string, string> {
  const [thumbnails, setThumbnails] = useState<Map<string, string>>(new Map());
  const [prevImageUrl, setPrevImageUrl] = useState(imageUrl);

  // Reset thumbnails synchronously when imageUrl changes to null (derived state during render)
  if (imageUrl !== prevImageUrl) {
    setPrevImageUrl(imageUrl);
    if (!imageUrl && thumbnails.size > 0) {
      setThumbnails(new Map());
    }
  }

  useEffect(() => {
    if (!imageUrl) return;

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    const handleLoad = (): void => {
      if (cancelled) return;

      const thumbMap = new Map<string, string>();
      for (const preset of EFFECT_PRESETS) {
        try {
          const dataUrl = generateFilteredThumbnail(img, preset);
          if (dataUrl) {
            thumbMap.set(preset.id, dataUrl);
          }
        } catch {
          // Skip failed thumbnails
        }
      }
      if (!cancelled) {
        setThumbnails(thumbMap);
      }
    };

    img.addEventListener('load', handleLoad);
    if (img.complete && !cancelled) {
      handleLoad();
    }

    return () => {
      cancelled = true;
      img.removeEventListener('load', handleLoad);
    };
  }, [imageUrl]);

  return thumbnails;
}
